import { BADGE_CLASS, BADGE_OUTLINE_CLASS, DOT_CLASS, statusInfo, type StatusKind } from '../lib/status';

const SIZE = { xs: 'badge-xs', sm: 'badge-sm', md: 'badge-md' } as const;

export interface StatusBadgeProps {
  kind: StatusKind;
  value: string | null | undefined;
  size?: keyof typeof SIZE;
  /** Last-known value from a stale observation: rendered as an outline and labelled. */
  stale?: boolean;
  /** Optional dimension label shown before the value, e.g. "DNS". */
  prefix?: string;
  className?: string;
}

/** A state value as a coloured badge that always carries its text (never colour alone). */
export function StatusBadge({ kind, value, size = 'sm', stale, prefix, className = '' }: StatusBadgeProps) {
  const info = statusInfo(kind, value);
  const cls = stale ? BADGE_OUTLINE_CLASS[info.tone] : BADGE_CLASS[info.tone];
  const hint = [info.hint, stale ? 'Last known value; the observation is stale.' : undefined].filter(Boolean).join(' ');
  return (
    <span className={`${cls} ${SIZE[size]} whitespace-nowrap ${className}`} title={hint || undefined} data-tone={info.tone}>
      <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current opacity-80" />
      {prefix ? <span className="opacity-70">{prefix}</span> : null}
      {info.label}
      {stale ? <span className="opacity-70">(last known)</span> : null}
    </span>
  );
}

/** A status dot followed by its text. */
export function StatusDot({ kind, value, className = '' }: { kind: StatusKind; value: string | null | undefined; className?: string }) {
  const info = statusInfo(kind, value);
  return (
    <span className={`inline-flex items-center gap-2 ${className}`} title={info.hint}>
      <span aria-hidden="true" className={`${DOT_CLASS[info.tone]} status-md`} />
      <span>{info.label}</span>
    </span>
  );
}
